import { Component, EventEmitter, forwardRef, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputMask } from 'primeng/inputmask';
import { ScrollerOptions } from 'primeng/api';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InputComponent implements ControlValueAccessor {
  @Input() inputType:
    | 'default'
    | 'date'
    | 'phone'
    | 'phone-v2'
    | 'number'
    | 'password'
    | 'text-area'
    | 'mask'
    | 'auto-complete'
    | 'editor'
    | 'otp' = 'default';
  @Input() input: string | number = '';
  @Input() id: string = '';
  @Input() locale!: string;
  @Input() currency!: string;
  @Input() currencyDisplay!: any;
  @Input() prefix!: string;
  @Input() suffix!: string;
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() labelColor: string = '';
  @Input() mask: string = '';
  @Input() pattern: string = '';
  @Input() invalid: boolean = false;
  @Input() required: boolean = false;
  @Input() autoResize: boolean = false;
  @Input() toggleMask: boolean = false;
  @Input() feedback: boolean = false;
  @Input() showClear: boolean = false;
  @Input() showButtons: boolean = false;
  @Input() autoClear: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() virtualScroll: boolean = false;
  @Input() lazy: boolean = false;
  @Input() completeOnFocus: boolean = false;
  @Input() multiple: boolean = false;
  @Input() integerOnly: boolean = false;
  @Input() unmask: boolean = true;
  @Input() fluid: boolean = true;
  @Input() size!: 'small' | 'large';
  @Input() variant: 'on' | 'in' | 'over' = 'on';
  @Input() labelType: 'default' | 'floating' = 'default';
  @Input() buttonLayout: 'stacked' | 'horizontal' | 'vertical' = 'stacked';
  @Input() mode: 'currency' | 'decimal' = 'decimal';
  @Input() minFractionDigits: number = 2;
  @Input() rows: number = 5;
  @Input() minlength!: number;
  @Input() maxlength!: number;
  @Input() pKeyFilter!: RegExp;
  @Input() mediumLabel: string = '';
  @Input() strongLabel: string = '';
  @Input() weakLabel: string = '';
  @Input() promptLabel: string = '';
  @Input() searchMessage: string = '';
  @Input() emptyMessage: string = '';
  @Input() suggestions: any[] = [];
  @Input() slotChar: string = '_';
  @Input() options!: any;
  @Input() optionLabel: string = 'name';
  @Input() optionValue?: string;
  @Input() virtualScrollOptions!: ScrollerOptions;
  @Input() virtualScrollItemSize!: number;
  @Input() delay!: number;
  @Input() length: number = 6;
  @Input() min!: number;
  @Input() max!: number;
  @Input() formControl?: FormControl;
  @Input() showErrors: boolean = true;
  @Input() errorMessage: string = '';

  @ViewChild('im') im?: InputMask;

  @Output() onValueChange = new EventEmitter();

  public selectedCountry: string = '';
  public phoneMaskTempValue: string = '';
  public otpTempValue: string = '';

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | number): void {
    this.input = value;
    if (this.input) {
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onchange(event: any): void {
    let trimmedValue = event;

    if (typeof trimmedValue === 'string') {
      trimmedValue = trimmedValue.trim();
    }

    if (this.inputType === 'editor' && typeof trimmedValue === 'string') {
      const cleanValue = trimmedValue.replace(/&nbsp;/g, ' ').replace(/ /g, ' ');
      this.onChange(cleanValue);
      this.onValueChange.emit(cleanValue);
      return;
    }

    this.input = trimmedValue;
    this.onChange(trimmedValue);
    this.onValueChange.emit(trimmedValue);
  }

  onBlur(): boolean {
    this.onTouched();
    return true;
  }

  selectPhone(event: any) {
    this.selectedCountry = event.value;

    if (this.selectedCountry) {
      this.mask = `+${this.selectedCountry} (99) 999 99 99`;
      // this.input = '';
    }
  }

  onInputMask(event: any) {
    const value = event.target.value?.replace(/\D/g, '') || '';
    if (value) {
      this.phoneMaskTempValue = value;
      if (this.im?.isCompleted()) {
        this.onchange(value);
      } else {
        this.onchange('');
      }
    } else {
      this.onchange('');
    }
  }

  getValueLen(v: any): number {
    if (v === null || v === undefined) return 0;
    if (Array.isArray(v)) return v.length;
    return String(v).length;
  }

  protected get hasError(): boolean {
    const ctrlInvalid = !!this.formControl?.invalid && (this.formControl?.touched || this.formControl?.dirty);
    return this.invalid || (this.showErrors && !!ctrlInvalid);
  }

  protected get errorI18nKey(): string | null {
    if (!this.hasError) return null;
    if (this.errorMessage) return null;

    const errors = this.formControl?.errors;
    if (!errors) return null;

    const first = Object.keys(errors)[0];
    if (!first) return null;

    return `input_errors.${first}`;
  }

  protected get errorI18nParams(): Record<string, any> | undefined {
    if (!this.hasError || !this.formControl?.errors) return undefined;
    const e = this.formControl.errors;
    if ('maxlength' in e) return { value: e['maxlength']?.requiredLength };
    if ('minlength' in e) return { value: e['minlength']?.requiredLength };
    if ('max' in e) return { value: e['max']?.max };
    if ('min' in e) return { value: e['min']?.min };
    return undefined;
  }

  protected handleTouched(): void {
    this.formControl?.markAsTouched();
    this.onTouched();
  }

  protected readonly oninput = oninput;
}
